import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import dicomParser from 'dicom-parser'
import { TAG_DICT } from '../home/dicom-viewer/dataDictionary'
import { FileService } from './file.service'
import { StringObjectLiteral } from '../util/util'

/**
 * Tag ids whose value is bulk pixel/waveform data — these must never be read as
 * a string: turning an MG image's multi-MB pixel data into a JS string blocks
 * the main thread and freezes the tab.
 */
const BULK_TAG_IDS = new Set<string>([
  'x7fe00010', // PixelData
  'x7fe00008', // FloatPixelData
  'x7fe00009', // DoubleFloatPixelData
  'x54001010', // WaveformData
])

/**
 * No real DICOM header string value approaches this size; anything larger is
 * bulk binary (pixel/overlay/icon data, large private blobs) and is skipped.
 */
const MAX_HEADER_VALUE_BYTES = 16 * 1024

export interface DicomTag {
  /** DICOM tag id, e.g. `(0018,7050)`. */
  tag: string
  /** Value Representation, e.g. `CS`. */
  vr: string
  /** Human-readable keyword, e.g. `FilterMaterial`. */
  name: string
  /** The tag's string value in the image. */
  value: string
}

/**
 * Reads DICOM header tag→value data from a parsed dataset, and (for views that
 * don't already have the image parsed) fetches + parses a stored `.dcm` on
 * demand. Extracted from `DicomViewerComponent.getAllTags` so the study view,
 * the phantom-test view, and any future view share one implementation and one
 * tag dictionary.
 */
@Injectable({ providedIn: 'root' })
export class DicomHeaderService {
  constructor(private http: HttpClient, private fileService: FileService) {}

  /** dicom-parser wants tag ids like `x00187050`; TAG_DICT stores `(0018,7050)`. */
  private toParserTagId(dictTag: string): string {
    return `x${dictTag.slice(1, 5)}${dictTag.slice(6, 10)}`.toLowerCase()
  }

  /**
   * True if this element is bulk/binary data (pixel data, overlays, icons,
   * large private blobs) that must not be read as a string.
   */
  private isBulkElement(
    tagId: string,
    element: { length?: number },
  ): boolean {
    if (BULK_TAG_IDS.has(tagId)) return true
    return (
      typeof element.length === 'number' &&
      element.length > MAX_HEADER_VALUE_BYTES
    )
  }

  /**
   * Every known tag that carries a string value in this dataset, as
   * `{tag, vr, name, value}`, sorted by tag id. Absent/empty and bulk/binary
   * tags (e.g. pixel data) are omitted.
   */
  getTags(dataSet: dicomParser.DataSet): DicomTag[] {
    const tags: DicomTag[] = []
    Object.values(TAG_DICT).forEach((entry) => {
      const tagId = this.toParserTagId(entry.tag)
      const element = dataSet.elements[tagId]
      // Only tags actually present in this image; never read pixel/bulk data.
      if (!element || this.isBulkElement(tagId, element)) return
      let value: string | undefined
      try {
        value = dataSet.string(tagId)
      } catch (e) {
        value = undefined
      }
      if (value !== undefined && value !== '') {
        tags.push({ tag: entry.tag, vr: entry.vr, name: entry.name, value })
      }
    })
    return tags.sort((a, b) => a.tag.localeCompare(b.tag))
  }

  /**
   * Map of tagName→value for ALL known tags (value may be `undefined`). Parity
   * with the original `DicomViewerComponent.getAllTags`, used for image overlays.
   */
  getAllTags(dataSet: dicomParser.DataSet): StringObjectLiteral<string> {
    const obj: StringObjectLiteral<string> = {}
    Object.values(TAG_DICT).forEach((entry) => {
      const tagId = this.toParserTagId(entry.tag)
      const element = dataSet.elements[tagId]
      // Skip bulk/binary elements (pixel data etc.): pointless for overlays and
      // reading them as a string can freeze the tab on MG images.
      if (element && this.isBulkElement(tagId, element)) return
      obj[entry.name] = dataSet.string(tagId)
    })
    return obj
  }

  /**
   * Fetch a stored `.dcm` by its file key, parse it, and return its populated
   * tags. Used by the phantom-test view, which shows server-rendered PNGs and
   * so does not already have the raw DICOM parsed in memory. Uses the same
   * `/files` route the image thumbnails load from (`withCredentials` so the
   * session is carried), then parses just the header client-side.
   */
  async fetchTags(dcmKey: string): Promise<DicomTag[]> {
    const url = this.fileService.getHostedUrl(dcmKey)
    const buf = await this.http
      .get(url, { responseType: 'arraybuffer', withCredentials: true })
      .toPromise()
    const dataSet = dicomParser.parseDicom(new Uint8Array(buf as ArrayBuffer))
    return this.getTags(dataSet)
  }
}
